import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { DbProfile } from "@/lib/types";

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<DbProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Calendar provider/tokens live in user_credentials (see useCalendar).
  const SAFE_PROFILE_COLUMNS = "id,agency_name,owner_name,owner_phone,agency_email,license_type,booking_link,business_address,business_city,business_state,business_zip,notify_email,notify_sms,service_specialties,specialty_preferences,ideal_client_acuity,ideal_client_age_range,ideal_client_hours_min,ideal_client_notes,ideal_client_payer_sources,reply_to_email,sender_display_name,segment,service_zips,golden_zips,priority_specialties,localities,primary_localities,waiver_types,bed_count,license_number,dbhds_region,hipaa_acknowledged,onboarding_dismissed_at,availability_settings,created_at,updated_at" as const;

  // Depend on the user's id, not the whole `user` object — Supabase issues a
  // new session/user object on every token refresh (e.g. right after the tab
  // regains focus) even though it's the same signed-in user. Depending on
  // the object reference would refetch (and flash `loading`) on every one of
  // those refreshes, which — via the app-wide `decidingAccess` gate in
  // App.tsx — remounted the whole route tree and wiped in-page state.
  const userId = user?.id;
  const fetchProfile = useCallback(async () => {
    if (!userId) { setProfile(null); setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase.from("profiles").select(SAFE_PROFILE_COLUMNS).eq("id", userId).single();
    setProfile(data as DbProfile | null);
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const updateProfile = useCallback(async (updates: Partial<DbProfile>) => {
    if (!user) return;
    const { error } = await supabase.from("profiles").update(updates).eq("id", user.id);
    if (error) throw error;
    await fetchProfile();
  }, [user, fetchProfile]);

  const isOnboarded = !!profile?.agency_name && !!profile?.owner_name && !!(profile as any)?.segment;

  // `loading` only flips false once fetchProfile's effect has actually run — but
  // when `user` changes (e.g. right after login) there's a render in between
  // where `user` is already the new user while `profile`/`loading` still reflect
  // the previous state. Treat a profile that doesn't belong to the current user
  // as loading too, so callers never see a stale profile for a new user.
  const effectiveLoading = loading || (!!user && profile?.id !== user.id);

  return { profile, loading: effectiveLoading, updateProfile, isOnboarded };
}
